<template>
    <c-button class="social-auth-button" :disabled="disabled || loading" @click="emit('click')">
        <b-spinner v-if="loading" small />
        <img v-else-if="icon" class="social-auth-button-icon" :src="icon" alt="social-logo" />
        <span class="social-auth-button-label">{{ label }}</span>
    </c-button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import googleLogo from '@/assets/images/google-logo.png';
import etsyLogo from '@/assets/images/etsy-logo.svg';
import tiktokLogo from '@/assets/images/tiktok-logo.svg';
import type { ExternalProvider } from '@/typings/auth/externalAuth';

const props = defineProps<{
    /** Omitted for the email option, which shares the provider card look without a logo. */
    provider?: ExternalProvider;
    label: string;
    loading?: boolean;
    disabled?: boolean;
}>();

const emit = defineEmits<{ (event: 'click'): void }>();

const icons: Record<ExternalProvider, string> = { google: googleLogo, etsy: etsyLogo, tiktok: tiktokLogo };
const icon = computed(() => props.provider && icons[props.provider]);
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

/* Neutral provider card from the Figma sign up / log in; `.btn` doubles the class to beat the nested bootstrap `.btn` rules */
.btn.social-auth-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 10px;
    background: get-color('white');
    border: 1px solid get-color('gray-light');
    border-radius: 8px;

    &:hover:not(:disabled) {
        background: #f5f5f5;
    }
}

.social-auth-button-icon {
    width: 28px;
    height: 28px;
    object-fit: contain;
}

.social-auth-button-label {
    font-size: 14px;
    font-weight: bold;
    line-height: normal;
    color: get-color('gray-600');
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
